/**
 * Mutation hook for updating the base selling price.
 *
 * Calls the upsert endpoint on the Tenant Server which auto-resolves
 * the storefront's active price list. Returns previous_rate for undo.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { frappeMethod } from "@/lib/erp/utils";

// ── Types ────────────────────────────────────────────────────

export interface UpdateItemPriceResponse {
  previous_rate: number | null;
  new_rate: number;
  item_price_name: string;
  price_list: string;
  currency: string;
}

// ── Hook ─────────────────────────────────────────────────────

export function useUpdateItemPrice(itemCode: string, variantOf?: string) {
  const queryClient = useQueryClient();

  return useMutation<UpdateItemPriceResponse, Error, number>({
    mutationFn: (rate: number) =>
      frappeMethod<UpdateItemPriceResponse>(
        "zaviago_backend.api.pricing_summary.update_item_base_price",
        { item_code: itemCode, rate }
      ),
    onSuccess: () => {
      // Invalidate queries for the current item (variant or template)
      queryClient.invalidateQueries({
        queryKey: ["pricing-summary", itemCode],
      });
      queryClient.invalidateQueries({
        queryKey: ["item", itemCode],
      });
      queryClient.invalidateQueries({
        queryKey: ["template-variants-summary", itemCode],
      });
      queryClient.invalidateQueries({
        queryKey: ["itemPricingTable", itemCode],
      });

      // OPS-1532: When saving a variant's price, also invalidate the parent
      // template's queries so the parent product view reflects the updated
      // price immediately without a manual page refresh.
      if (variantOf) {
        queryClient.invalidateQueries({
          queryKey: ["pricing-summary", variantOf],
        });
        queryClient.invalidateQueries({
          queryKey: ["item", variantOf],
        });
        queryClient.invalidateQueries({
          queryKey: ["template-variants-summary", variantOf],
        });
        queryClient.invalidateQueries({
          queryKey: ["itemPricingTable", variantOf],
        });
      }
    },
  });
}
